import { ChangeDetectorRef, Component, DestroyRef, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FixturePage } from '@cds/betting-offer';
import { DayOfRaceDate, TimeGroupedMeetings } from '@cds/betting-offer/domain-specific/horse-racing';
import { Meeting } from '@cds/betting-offer/tags';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { translationToStr } from '@frontend/sports/common/core/utils/translations';
import { Observable } from 'rxjs';

import { convertDayOfRaceToUrlParam } from '../../../horse-racing-shared/helpers';
import { RedirectHelperService } from '../../../navigation-core/redirect-helper.service';
import { Events } from '../../models/events';

@Component({
    template: '',
    standalone: false,
})
export class HorsesRaceTopnavBaseComponent {
    @Input() meeting: Meeting;
    @Input() dayOfRace: DayOfRaceDate;

    tournamentSelectorOpen = false;
    meetings: Nullable<TimeGroupedMeetings>;

    constructor(
        protected redirectHelper: RedirectHelperService,
        protected dispatcherService: DispatcherService,
        protected changeDetector: ChangeDetectorRef,
        protected destroyRef: DestroyRef,
    ) {}

    protected topNavOnInit(racingService: Observable<TimeGroupedMeetings>, futureFixture?: FixturePage): void {
        this.meetings = null;
        racingService.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
            this.meetings = data;
            this.changeDetector.markForCheck();
        });

        this.dispatcherService
            .on(Events.HorseRacing.MeetingSelected)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => {
                this.onMeetingSelected(this.meeting, data, this.meetings, futureFixture);
            });

        // Make ability to close meeting selector from outside.
        this.dispatcherService
            .on(Events.HorseRacing.CloseMeetingSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.tournamentSelectorOpen) {
                    this.tournamentSelectorOpen = false;
                    this.changeDetector.markForCheck();
                }
            });
    }

    toggleTournamentSelector(
        meetings: Nullable<TimeGroupedMeetings>,
        meeting: Meeting,
        dayOfRace: DayOfRaceDate,
        futureMeetings?: FixturePage,
    ): void {
        if (!meetings) {
            return;
        }

        this.tournamentSelectorOpen = !this.tournamentSelectorOpen;
        if (this.tournamentSelectorOpen) {
            this.dispatcherService.dispatch(Events.HorseRacing.OpenMeetingSelector, {
                meeting,
                meetings,
                dayOfRace,
                futureMeetings,
            });
        } else {
            this.dispatcherService.dispatch(Events.HorseRacing.CloseMeetingSelector);
        }
    }

    private onMeetingSelected(
        meeting: Nullable<Meeting>,
        data: { meeting: Meeting; dayOfRaceDate: DayOfRaceDate },
        meetings: Nullable<TimeGroupedMeetings>,
        futureData?: FixturePage,
    ): void {
        this.toggleTournamentSelector(meetings, data.meeting, data.dayOfRaceDate, futureData);

        if (meeting === data.meeting) {
            return;
        }
        meeting = data.meeting;
        const param = {
            id: data.meeting.id,
            name: translationToStr(data.meeting.name),
        };
        this.redirectHelper.goToHorseRacing(param, convertDayOfRaceToUrlParam(data.dayOfRaceDate));
    }
}
